/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { useState, Fragment, useContext, useEffect } from 'react';
import clsx from 'clsx';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import logoDarkSrc from '../../assets/images/logosAzn/logo.png';
import logoWhiteSrc from '../../assets/images/logosAzn/logoWhite.png';
import logoNoogaDarkSrc from '../../assets/images/logoNooga/logo_nooga_dark.png';
import logoNoogaLightSrc from '../../assets/images/logoNooga/logo_nooga_light.png';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectRouterState } from '../../data/store-selectors';
import { getNavigationItemsConfig, NavBarType, NavigationItem } from './navBarConfig';
import ListItem from '@mui/material/ListItem';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { actions } from 'redux-router5';
import { useTranslation } from 'react-i18next';
import { startsWithSegment } from 'router5-helpers';
import Icon from '@mui/material/Icon';
import { AppAbilityContext } from '../../accessibility/ability';
import { RouteId } from '../../router/routes';

export const drawerWidth = 245;
export const drawerCloseWidth = 62;

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        drawer: {
            width: drawerWidth,
            flexShrink: 0,
            whiteSpace: 'nowrap',
        },
        drawerOpen: {
            background: theme.palette.secondary.main,
            width: drawerWidth,
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
        },
        drawerClose: {
            background: theme.palette.secondary.main,
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
            overflowX: 'hidden',
            width: drawerCloseWidth,
        },
        toolbar: {
            background: theme.palette.secondary.light,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            ...theme.mixins.toolbar,
        },
        burgerButton: {
            padding: theme.spacing(1.4),
            margin: theme.spacing(1),
        },
        logo: {
            left: 78,
            top: 14,
            height: 35,
            width: 116,
        },
        logoOpen: {
            position: 'absolute',
        },
        logoClose: {
            position: 'fixed',
        },
        logoNooga: {
            top: 12,
            width: 60,
            height: 40,
        },
        logoNoogaOpenDrawer: {
            position: 'absolute',
            transition: theme.transitions.create('left', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
            left: 164,
        },
        logoNoogaCloseDrawer: {
            position: 'fixed',
            transition: theme.transitions.create('left', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
            left: 78,
        },
        expandIcon: {
            color: 'white',
            position: 'absolute',
            left: 0,
            width: 18,
        },
        navList: {
            paddingTop: 0,
            margin: theme.spacing(1),
        },
        bottomNavList: {
            paddingTop: 0,
            margin: theme.spacing(1),
            display: 'flex',
            alignItems: 'flex-end',
            flexGrow: 1,
        },
        listItem: {
            borderRadius: 5,
            padding: theme.spacing(1, 1, 1, 1.4),
            marginBottom: theme.spacing(1),
            '&:hover': {
                backgroundColor: 'rgba(88, 184, 219, 0.4)',
            },
            '&.Mui-selected': {
                backgroundColor: 'rgba(88, 184, 219, 0.4)',
                '&:hover': {
                    backgroundColor: 'rgba(88, 184, 219, 0.4)',
                },
            },
        },
        listItemIcon: {
            minWidth: 0,
        },
        listItemIconNestedParent: {
            marginLeft: theme.spacing(0.5),
        },
        icon: {
            color: 'white',
        },
        listItemText: {
            color: 'white',
            marginLeft: theme.spacing(2.2),
        },
        nestedListItem: {
            paddingLeft: theme.spacing(2.2),
        },
        nestedIcon: {
            fontSize: 20,
        },
    }),
);

interface NavBarProps {
    navBarType: NavBarType;
    isOpenNav: boolean;
    isMatchesSM: boolean;
    navBarToggle(): void;
}

export const NavBar = (props: NavBarProps) => {
    const { navBarType, isOpenNav, navBarToggle, isMatchesSM } = props;

    const dispatch = useDispatch();
    const classes = useStyles();
    const { t } = useTranslation();

    const routerState = useSelector(selectRouterState, shallowEqual);
    const ability = useContext(AppAbilityContext);
    const [navItems, setNavItems] = useState<NavigationItem[]>([]);

    const [isOpenRegistryWrapper, setIsOpenRegistryWrapper] = useState(
        startsWithSegment(routerState.route.name, RouteId.Registry),
    );
    const [isOpenInfographicWrapper, setIsOpenInfographicWrapper] = useState(
        startsWithSegment(routerState.route.name, RouteId.Infographic),
    );
    const [isOpenMapWrapper, setIsOpenMapWrapper] = useState(startsWithSegment(routerState.route.name, RouteId.Map));

    let documentSearchItem: NavigationItem = null;

    useEffect(() => {
        const items: NavigationItem[] = getNavigationItemsConfig(navBarType, ability);
        setNavItems(items);
    }, []);

    const handleClickNavWrapper = (item: NavigationItem) => {
        switch (item.permit) {
            case 'INFOGRAPHICS_WRAPPER':
                setIsOpenInfographicWrapper(!isOpenInfographicWrapper);
                break;
            case 'MAPS_WRAPPER':
                setIsOpenMapWrapper(!isOpenMapWrapper);
                break;
            case 'REGISTRY_WRAPPER':
                setIsOpenRegistryWrapper(!isOpenRegistryWrapper);
                break;
        }
    };

    const isNavWrapperOpen = (item: NavigationItem): boolean => {
        switch (item.permit) {
            case 'INFOGRAPHICS_WRAPPER':
                return isOpenInfographicWrapper;
            case 'MAPS_WRAPPER':
                return isOpenMapWrapper;
            case 'REGISTRY_WRAPPER':
                return isOpenRegistryWrapper;
            default:
                return false;
        }
    };

    const renderNavItem = (item: NavigationItem, isNested: boolean) => {
        return (
            <ListItem
                className={clsx(classes.listItem, isNested ? classes.nestedListItem : null)}
                key={item.routeId}
                button
                onClick={() => {
                    dispatch(actions.navigateTo(item.routeId));
                }}
                selected={startsWithSegment(routerState.route.name, item.routeId)}
            >
                <ListItemIcon className={classes.listItemIcon}>
                    <Icon className={clsx(classes.icon, isNested ? classes.nestedIcon : null)}>{item.icon}</Icon>
                </ListItemIcon>
                <ListItemText primary={t(item.label)} className={classes.listItemText} />
            </ListItem>
        );
    };

    return (
        <Drawer
            variant={isMatchesSM ? 'temporary' : 'permanent'}
            open={isOpenNav}
            className={
                !isMatchesSM
                    ? clsx(classes.drawer, {
                          [classes.drawerOpen]: isOpenNav,
                          [classes.drawerClose]: !isOpenNav,
                      })
                    : classes.drawer
            }
            classes={{
                paper: clsx({
                    [classes.drawerOpen]: isOpenNav,
                    [classes.drawerClose]: !isOpenNav,
                }),
            }}
            onClose={navBarToggle}
        >
            <div className={classes.toolbar}>
                <IconButton className={classes.burgerButton} onClick={navBarToggle} size="large">
                    <MenuIcon style={{ color: 'white' }} />
                </IconButton>

                {process.env.REACT_APP_VARIANT === 'nooga' ? (
                    <img
                        className={clsx(classes.logoNooga, {
                            [classes.logoNoogaOpenDrawer]: isOpenNav,
                            [classes.logoNoogaCloseDrawer]: !isOpenNav,
                        })}
                        src={!isOpenNav ? logoNoogaDarkSrc : logoNoogaLightSrc}
                        alt="logo"
                    />
                ) : (
                    <img
                        className={clsx(classes.logo, {
                            [classes.logoOpen]: isOpenNav,
                            [classes.logoClose]: !isOpenNav,
                        })}
                        src={!isOpenNav ? logoDarkSrc : logoWhiteSrc}
                        alt="logo"
                    />
                )}
            </div>

            <Divider />

            <List component="nav" className={classes.navList}>
                {navItems.map((item) => {
                    if (item.hasOwnProperty('nested')) {
                        return (
                            <Fragment key={item.routeId}>
                                <ListItem
                                    className={classes.listItem}
                                    button
                                    onClick={() => {
                                        handleClickNavWrapper(item);
                                    }}
                                >
                                    {isNavWrapperOpen(item) ? (
                                        <ExpandLess className={classes.expandIcon} />
                                    ) : (
                                        <ExpandMore className={classes.expandIcon} />
                                    )}

                                    <ListItemIcon
                                        className={clsx(classes.listItemIcon, classes.listItemIconNestedParent)}
                                    >
                                        <Icon style={{ color: 'white' }}>{item.icon}</Icon>
                                    </ListItemIcon>
                                    <ListItemText primary={t(item.label)} className={classes.listItemText} />
                                </ListItem>
                                <Collapse key={item.routeId} in={isNavWrapperOpen(item)} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        {item.nested.map((nestedItem) => renderNavItem(nestedItem, true))}
                                    </List>
                                </Collapse>
                            </Fragment>
                        );
                    } else {
                        if (item.permit === 'DOCUMENT_PICTURE_SCREEN') {
                            documentSearchItem = item;
                            return null;
                        } else return renderNavItem(item, false);
                    }
                })}
            </List>

            {documentSearchItem && (
                <List key={documentSearchItem.routeId} component="nav" className={classes.bottomNavList}>
                    {renderNavItem(documentSearchItem, false)}
                </List>
            )}
        </Drawer>
    );
};
export { NavBarType };
